import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  Home, 
  Image, 
  History, 
  Settings, 
  LogOut, 
  User,
  Menu,
  X,
  Camera,
  MessageCircle,
  Bot
} from 'lucide-react';
import { useAuthStore } from '../stores/authStore';
import ContactModal from './ContactModal';
import toast from 'react-hot-toast';

const Layout = ({ children }) => {
  const { user, logout, getFlickrAccount } = useAuthStore();
  const flickrAccount = getFlickrAccount();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [contactModalOpen, setContactModalOpen] = useState(false);

  // Set document title dynamically
  useEffect(() => {
    const appName = process.env.REACT_APP_NAME || 'Lenscribe';
    document.title = `${appName} - AI-Powered Photo Metadata Enhancement`;
  }, []);

  const navigation = [
    { name: 'Dashboard', href: '/', icon: Home },
    { name: 'Photo Selection', href: '/photos', icon: Image },
    { name: 'Agent', href: '/agent', icon: Bot },
    { name: 'Job History', href: '/history', icon: History },
    { name: 'Settings', href: '/settings', icon: Settings },
  ];

  const handleLogout = async () => {
    try {
      await logout();
      toast.success('Logged out successfully');
      navigate('/'); // Redirect to home page instead of login
    } catch (error) {
      toast.error('Failed to logout');
    }
  };

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile menu */}
      <div className={`fixed inset-0 z-50 lg:hidden ${mobileMenuOpen ? 'block' : 'hidden'}`}>
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setMobileMenuOpen(false)} />
        <div className="fixed inset-y-0 left-0 flex w-64 flex-col bg-white">
          <div className="flex h-16 items-center justify-between px-4">
            <div className="flex items-center">
              <Camera className="h-8 w-8 text-primary-600" />
              <span className="ml-2 text-xl font-bold text-gray-900">{process.env.REACT_APP_NAME || 'Lenscribe'}</span>
            </div>
            <button
              onClick={() => setMobileMenuOpen(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
          <nav className="flex-1 space-y-1 px-2 py-4">
            {navigation.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid={`mobile-nav-${item.name.toLowerCase().replace(' ', '-')}`}
                  className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                    isActive(item.href)
                      ? 'bg-primary-100 text-primary-900'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                >
                  <Icon className="mr-3 h-5 w-5" />
                  {item.name}
                </Link>
              );
            })}
          </nav>
          <div className="border-t border-gray-200 p-4">
            {/* Contact Support Button */}
            <button
              onClick={() => {
                setContactModalOpen(true);
                setMobileMenuOpen(false);
              }}
              className="mb-4 flex w-full items-center px-2 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-900 rounded-md transition-colors"
            >
              <MessageCircle className="mr-3 h-5 w-5" />
              Contact Support
            </button>
            
            <Link to="/account" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-account-link" className="flex items-center hover:bg-gray-50 rounded-md px-2 py-2 transition-colors">
              <div className="flex-shrink-0">
                {flickrAccount?.avatarUrl ? (
                  <img
                    className="h-8 w-8 rounded-full"
                    src={flickrAccount.avatarUrl}
                    alt={flickrAccount.displayName}
                  />
                ) : (
                  <User className="h-8 w-8 text-gray-400" />
                )}
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-700">{flickrAccount?.displayName || user?.email}</p>
                <p className="text-xs text-gray-500">{flickrAccount?.username || 'No photo provider connected'}</p>
              </div>
            </Link>
            <button
              onClick={handleLogout}
              className="mt-3 flex w-full items-center px-2 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-900 rounded-md"
            >
              <LogOut className="mr-3 h-5 w-5" />
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex flex-col flex-grow bg-white border-r border-gray-200">
          <div className="flex h-16 items-center px-4">
            <Camera className="h-8 w-8 text-primary-600" />
            <span className="ml-2 text-xl font-bold text-gray-900">{process.env.REACT_APP_NAME || 'Lenscribe'}</span>
          </div>
          <nav className="flex-1 space-y-1 px-2 py-4">
            {navigation.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  data-testid={`desktop-nav-${item.name.toLowerCase().replace(' ', '-')}`}
                  className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                    isActive(item.href)
                      ? 'bg-primary-100 text-primary-900'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                >
                  <Icon className="mr-3 h-5 w-5" />
                  {item.name}
                </Link>
              );
            })}
          </nav>
          <div className="border-t border-gray-200 p-4">
            {/* Contact Support Button */}
            <button
              onClick={() => setContactModalOpen(true)}
              className="mb-4 flex w-full items-center px-2 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-900 rounded-md transition-colors"
            >
              <MessageCircle className="mr-3 h-5 w-5" />
              Contact Support
            </button>
            
            <Link to="/account" data-testid="desktop-account-link" className="flex items-center hover:bg-gray-50 rounded-md px-2 py-2 transition-colors">
              <div className="flex-shrink-0">
                {flickrAccount?.avatarUrl ? (
                  <img
                    className="h-8 w-8 rounded-full"
                    src={flickrAccount.avatarUrl}
                    alt={flickrAccount.displayName}
                  />
                ) : (
                  <User className="h-8 w-8 text-gray-400" />
                )}
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-700">{flickrAccount?.displayName || user?.email}</p>
                <p className="text-xs text-gray-500">{flickrAccount?.username || 'No photo provider connected'}</p>
              </div>
            </Link>
            <button
              onClick={handleLogout}
              className="mt-3 flex w-full items-center px-2 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-900 rounded-md"
            >
              <LogOut className="mr-3 h-5 w-5" />
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Mobile header */}
        <div className="sticky top-0 z-40 flex h-16 items-center gap-x-4 border-b border-gray-200 bg-white px-4 shadow-sm lg:hidden">
          <button
            onClick={() => setMobileMenuOpen(true)}
            data-testid="mobile-menu-button"
            className="text-gray-400 hover:text-gray-600"
          >
            <Menu className="h-6 w-6" />
          </button>
          <div className="flex items-center">
            <Camera className="h-6 w-6 text-primary-600" />
            <span className="ml-2 text-lg font-semibold text-gray-900">{process.env.REACT_APP_NAME || 'Lenscribe'}</span>
          </div>
        </div>

        {/* Page content */}
        <main className="py-6">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            {children}
          </div>
        </main>
      </div>

      {/* Contact Modal */}
      <ContactModal 
        isOpen={contactModalOpen} 
        onClose={() => setContactModalOpen(false)} 
        user={user} 
      />
    </div>
  );
};

export default Layout;
